import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions
} from '@angular/material/dialog';
import { DocketService } from '../..';
import { ConfigService } from '../../services/config.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { filter, map, startWith, tap } from 'rxjs/operators';
import {
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { BehaviorSubject, withLatestFrom } from 'rxjs';
import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker
} from '@angular/material/datepicker';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { NgIf, NgFor, AsyncPipe, PercentPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import {
  MatFormField,
  MatSuffix,
  MatLabel
} from '@angular/material/form-field';
import { AlertTextComponent } from '../../alert-text/alert-text.component';
import { startOfDay } from 'date-fns';
import moment, { Moment } from 'moment';
@Component({
  selector: 'app-docket-settings-dialog',
  templateUrl: './docket-settings-dialog.component.html',
  styleUrls: ['./docket-settings-dialog.component.css'],
  providers: [DocketService],
  standalone: true,
  imports: [
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatInput,
    NgIf,
    MatSelect,
    NgFor,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatLabel,
    MatDialogActions,
    MatButton,
    AsyncPipe,
    AlertTextComponent,
    PercentPipe
  ]
})
export class DocketSettingsDialogComponent {
  form = new FormGroup({
    tax_rate: new FormControl(0.2),
    purchase_order: new FormControl(null),
    account: new FormControl(null, Validators.required),
    rollover_date: new FormControl<moment.Moment | null>(null),
    currency_code: new FormControl('GBP'),
    currency_rate: new FormControl(1)
  });

  shouldShowRolloverWarning$ = this.form.controls[
    'rollover_date'
  ].valueChanges.pipe(
    filter((rolloverDate) => rolloverDate !== null),
    map((rolloverDate) => {
      return (
        rolloverDate &&
        this.data.docket.rolled_over_to === null &&
        rolloverDate.toDate().valueOf() <= new Date().valueOf()
      );
    })
  );

  invoiceTypes = [
    { name: 'Account', ref: 1 },
    { name: 'Cash Invoice', ref: 0 }
  ];

  title = 'Edit Hire Contract';
  submitting = false;
  isInvoice = false;
  isAccountCustomer$ = this.form.controls['account'].valueChanges.pipe(
    map((account) => {
      this.data.docket.customer &&
        account === 1 &&
        !this.data.docket.customer.account_number &&
        !this.data.docket.customer.xero_id;
    }),
    startWith(false)
  );
  isDocketLockedAfterInvoice$ = this.configService.userConfig.pipe(
    map((config) => config['docket_locked_after_invoice'] === true)
  );

  taxRates$ = this.configService.taxRates.pipe(
    tap((taxRates) => {
      if (taxRates.length && this.data.confirmInvoiceDetails) {
        this.form.patchValue({ tax_rate: taxRates[0].value });
      }
    })
  );

  hasAccount = new BehaviorSubject(false);
  hasAccount$ = this.hasAccount.asObservable();

  constructor(
    public dialogRef: MatDialogRef<DocketSettingsDialogComponent>,
    private configService: ConfigService,
    @Inject(MAT_DIALOG_DATA) public data,
    private docketService: DocketService,
    private toastrService: ToastrService
  ) {
    if (data.docket) {
      this.form.patchValue({
        tax_rate: data.docket.tax_rate,
        purchase_order: data.docket.purchase_order,
        account: data.docket.account,
        rollover_date: data.docket.rollover_date
          ? moment(data.docket.rollover_date)
          : null,
        currency_code: data.docket.currency_code,
        currency_rate: data.docket.currency_rate
      });
      if (data.docket.account_reference) {
        this.title = 'Edit Invoice';
      }
      if (data.docket.rolled_over_to) {
        this.form.controls['rollover_date'].disable();
      }
    }
    if (data.confirmInvoiceDetails) {
      this.form.patchValue({ account: null, tax_rate: null });
      this.title = 'Confirm Invoice Details';
    }

    this.determineHasAccount();
    this.form.controls['currency_code'].valueChanges
      .pipe(
        startWith(this.form.controls['currency_code'].value),
        withLatestFrom(this.defaultCurrency$)
      )
      .subscribe(([current, defaultCurrency]) => {
        const currencyRateField = this.form.controls['currency_rate'];
        if (current === defaultCurrency) {
          currencyRateField.setValue(1);
          currencyRateField.disable();
          currencyRateField.setValidators(null);
        } else {
          currencyRateField.enable();
          currencyRateField.setValidators(Validators.required);
          if (currencyRateField.value === 1) {
            currencyRateField.patchValue(null);
          }
        }
      });
  }

  determineHasAccount() {
    const integrationRelationships =
      this.data.docket.customer.integration_relationships;
    const hasExternalAccount =
      integrationRelationships.length > 0 || this.data.docket.customer.xero_id;
    const hasAccount =
      hasExternalAccount || Boolean(this.data.docket.customer.account_number);
    this.hasAccount.next(hasAccount);
    this.form.controls['account'].setValue(hasAccount ? 1 : 0);
  }

  get defaultCurrency$() {
    return this.configService.userConfig.pipe(
      startWith({ currency: 'GBP' }),
      filter((config) => 'currency' in config),
      map((config) => config['currency'])
    );
  }

  get currencies$() {
    return this.configService.userConfig.pipe(
      startWith({}),
      filter((config) => 'additional_currencies' in config),
      map((config) =>
        [config['currency'] ?? 'GBP'].concat(
          config['additional_currencies']
            .split(',')
            .map((value) => value.trim())
        )
      )
    );
  }

  submit() {
    this.submitting = true;

    const rolloverDate = this.form.controls['rollover_date'].value;
    const submitData = {
      ...this.form.value,
      rollover_date: rolloverDate
        ? this.isTodaysDate(rolloverDate.toDate())
          ? new Date()
          : rolloverDate.toDate()
        : null
    };
    this.docketService.update(this.data.docket.id, submitData).subscribe({
      next: (success) => {
        Object.assign(this.data.docket, this.form.value);
        this.dialogRef.close({
          convertToInvoice: this.data?.confirmInvoiceDetails
        });
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 422) {
          this.toastrService.warning(
            error.error?.message ?? 'Please contact support',
            'Could not rollover invoice'
          );
        }
        this.submitting = false;
      },
      complete: () => {
        this.submitting = false;
      }
    });
  }

  isTodaysDate(date: Date) {
    return startOfDay(date).valueOf() === startOfDay(new Date()).valueOf();
  }
}
